import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Chip,
    Divider,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Select,
    MenuItem,
    FormControl,
    InputLabel
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    Pending as PendingIcon,
    Error as ErrorIcon,
    ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { useCimbeo } from '../../../../context';
import PercentageBar from '../../../../components/PercentageBar';

const REQUIREMENT_STEPS = [
    { id: 'importing', label: 'Importing', duration: 1000 },
    { id: 'normalizing', label: 'Normalizing', duration: 2000 },
    { id: 'summarizing', label: 'Summarizing', duration: 1000 },
    { id: 'contentAnalysis', label: 'Content Analysis', duration: 8000 },
    { id: 'contentCleaning', label: 'Content Cleaning', duration: 2000 },
    { id: 'missionNeed', label: 'Mission Need Extraction', duration: 3000 },
    { id: 'capability', label: 'Capability Extraction', duration: 3000 },
    { id: 'conops', label: 'CONOPS Extraction', duration: 4000 },
    { id: 'requirements', label: 'Requirements Extraction', duration: 5000 }
];

const ASSET_STEPS = [
    { id: 'importing', label: 'Importing', duration: 1000 },
    { id: 'normalizing', label: 'Normalizing', duration: 2000 },
    { id: 'summarizing', label: 'Summarizing', duration: 1000 },
    { id: 'contentAnalysis', label: 'Content Analysis', duration: 8000 },
    { id: 'contentCleaning', label: 'Content Cleaning', duration: 2000 },
    { id: 'assetExtraction', label: 'Asset Extraction', duration: 5000 }
];

function DocumentProcessing() {
    const { ingestedDocuments, setIngestedDocuments, projectName } = useCimbeo();
    const [expandedDoc, setExpandedDoc] = useState(null);
    const [selectedProject, setSelectedProject] = useState(projectName || '');

    useEffect(() => {
        // Get unique projects from documents
        const projects = [...new Set(ingestedDocuments.map(doc => doc.projectName))];
        if (projects.length > 0 && !selectedProject) {
            setSelectedProject(projects[0]);
        }
    }, [ingestedDocuments, selectedProject]);

    useEffect(() => {
        // Simulate processing for documents that are still processing
        const processingDocs = ingestedDocuments.filter(doc => doc.status === 'processing');
        if (processingDocs.length > 0) {
            // Set the current step and start timing
            const interval = setInterval(() => {
                setIngestedDocuments(prev => {
                    return prev.map(doc => {
                        if (doc.status === 'processing') {
                            const steps = doc.type === 'requirements' ? REQUIREMENT_STEPS : ASSET_STEPS;
                            const currentStepIndex = steps.findIndex(step => !doc.completedSteps.includes(step.id));
                            
                            if (currentStepIndex === -1) {
                                return { ...doc, status: 'completed' };
                            }

                            // If no current step is set, start a new one
                            if (!doc.currentStep) {
                                return {
                                    ...doc,
                                    currentStep: steps[currentStepIndex].id,
                                    stepStartTime: Date.now()
                                };
                            }

                            // Check if current step should be completed
                            const currentStep = steps.find(step => step.id === doc.currentStep);
                            if (!currentStep) return doc;
                            
                            const elapsedTime = Date.now() - Number(doc.stepStartTime);

                            if (elapsedTime >= currentStep.duration) {
                                return {
                                    ...doc,
                                    completedSteps: [...doc.completedSteps, doc.currentStep],
                                    currentStep: null,
                                    stepStartTime: null
                                };
                            }
                        }
                        return doc;
                    });
                });
            }, 100); // Check frequently for step updates

            return () => clearInterval(interval);
        }
    }, [ingestedDocuments, setIngestedDocuments]);

    const getStepStatus = (doc, stepId) => {
        if (doc.completedSteps.includes(stepId)) return 'completed';
        if (doc.currentStep === stepId) return 'processing';
        if (doc.status === 'error') return 'error';
        return 'pending';
    };

    const getStepIcon = (status) => {
        switch (status) {
            case 'completed':
                return <CheckCircleIcon backgroundColor="gold.main" />;
            case 'processing':
                return <PendingIcon color="grey-100" />;
            case 'error':
                return <ErrorIcon color="error" />;
            default:
                return <PendingIcon color="disabled" />;
        }
    };

    const getOverallProgress = (doc) => {
        const steps = doc.type === 'requirements' ? REQUIREMENT_STEPS : ASSET_STEPS;
        return Math.round((doc.completedSteps.length / steps.length) * 100);
    };

    const getStepProgress = (doc, stepId) => {
        if (doc.completedSteps.includes(stepId)) return 100;
        if (doc.currentStep === stepId && doc.stepStartTime) {
            const steps = doc.type === 'requirements' ? REQUIREMENT_STEPS : ASSET_STEPS;
            const currentStep = steps.find(step => step.id === stepId);
            if (!currentStep) return 0;
            
            const elapsedTime = Date.now() - Number(doc.stepStartTime);
            const progress = Math.min(Math.round((elapsedTime / currentStep.duration) * 100), 100);
            console.log('Progress for step', stepId, ':', progress);
            return isNaN(progress) ? 0 : progress;
        }
        return 0;
    };

    const getRemainingTime = (doc, stepId) => {
        if (doc.currentStep === stepId && doc.stepStartTime) {
            const steps = doc.type === 'requirements' ? REQUIREMENT_STEPS : ASSET_STEPS;
            const currentStep = steps.find(step => step.id === stepId);
            const elapsedTime = Date.now() - doc.stepStartTime;
            const remainingTime = Math.max(0, currentStep.duration - elapsedTime);
            return `${Math.ceil(remainingTime / 1000)}s`;
        }
        return '';
    };

    const handleAccordionChange = (docId) => (event, isExpanded) => {
        setExpandedDoc(isExpanded ? docId : null);
    };

    const handleProjectChange = (event) => {
        setSelectedProject(event.target.value);
    };

    const filteredDocuments = ingestedDocuments.filter(doc => doc.projectName === selectedProject);

    return (
        <Box sx={{ 
            width: '100%', 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            backgroundColor: '#dee2e6',
            py: 1
          }}>
            <Box sx={{
              width: '98%',
              backgroundColor: 'navy.main',
              py: 3,
              borderRadius: 2,
              px: 2
            }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2}}>
                    <Typography variant="h5" color="text.main">
                        Document Processing Status
                    </Typography>
                    <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel>Select Project</InputLabel>
                        <Select
                            value={selectedProject}
                            label="Select Project"
                            onChange={handleProjectChange}
                            sx={{
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#2B5587',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#1a3a5f',
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#2B5587',
                                },
                            }}
                        >
                            {[...new Set(ingestedDocuments.map(doc => doc.projectName))].map(project => (
                                <MenuItem key={project} value={project}>
                                    {project}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
                
                {filteredDocuments.length === 0 ? (
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography color="text.secondary">
                            No documents are currently being processed for this project.
                        </Typography>
                    </Paper>
                ) : (
                    <List>
                        {filteredDocuments.map((doc, index) => (
                            <React.Fragment key={doc.id}>
                                <Accordion
                                    expanded={expandedDoc === doc.id}
                                    onChange={handleAccordionChange(doc.id)}
                                    sx={{ mb: 1 }}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        sx={{
                                            '& .MuiAccordionSummary-content': {
                                                margin: '12px 0',
                                            }
                                        }}
                                    >
                                        <Box sx={{ width: '100%' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="subtitle1">
                                                    {doc.name}
                                                </Typography>
                                                <Chip
                                                    label={doc.type === 'requirements' ? 'Requirements' : 'Asset'}
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                />
                                            </Box>
                                            <Box sx={{ mt: 1 }}>
                                                <PercentageBar
                                                    value={getOverallProgress(doc)}
                                                    color={getOverallProgress(doc) === 100 ? 'gold.main' : '#BDF2EE'}
                                                />
                                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                                    {doc.completedSteps.length} of {doc.type === 'requirements' ? REQUIREMENT_STEPS.length : ASSET_STEPS.length} steps completed
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <List>
                                            {(doc.type === 'requirements' ? REQUIREMENT_STEPS : ASSET_STEPS).map(step => (
                                                <ListItem key={step.id}>
                                                    <ListItemIcon>
                                                        {getStepIcon(getStepStatus(doc, step.id))}
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Typography>{step.label}</Typography>
                                                            </Box>
                                                        }
                                                        secondary={
                                                            <Box sx={{ mt: 1 }}>
                                                                <PercentageBar
                                                                    value={getStepProgress(doc, step.id)}
                                                                    color={getStepStatus(doc, step.id) === 'completed' ? 'gold.main' : '#BDF2EE'}
                                                                    height={4}
                                                                />
                                                            </Box>
                                                        }
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </AccordionDetails>
                                </Accordion>
                                {index < filteredDocuments.length - 1 && <Divider />}
                            </React.Fragment>
                        ))}
                    </List>
                )}
            </Box>
        </Box>
    );
}

export default DocumentProcessing; 